import { Controller } from "@hotwired/stimulus";

// Which drawer holds the current page is decided server-side (Admin::NavHelper) and arrives
// as data-nav-expanded on the open drawer, so no URL is named here. This controller owns only
// what the server cannot: toggling drawers on click, and the button styling that pairs with an
// already-open drawer.
//
// Every lookup is scoped to this.element because the admin layout renders the whole nav twice,
// once for the desktop sidebar and once for the mobile drawer. The ids repeat across the two,
// so document.getElementById hands both instances the desktop copy and leaves the mobile drawer
// inert.
export default class extends Controller {
  connect() {
    Turbo.cache.exemptPageFromPreview()

    this.element.addEventListener("click", this.disableButton.bind(this));
    this.element.addEventListener("click", this.handleDropdownClick.bind(this));
    this.setExpanded();
  }

  // Every drawer in this nav, paired with the button that toggles it. The two ids differ only
  // in their suffix, which is what lets the pairing be read off the markup.
  dropdowns() {
    return Array.from(this.element.querySelectorAll("ul[id^='dropdown-'][id$='-menu']"))
      .map((menu) => ({ menu, button: this.buttonFor(menu) }))
      .filter(({ button }) => button);
  }

  buttonFor(menu) {
    return this.element.querySelector(`[id="${menu.id.replace(/-menu$/, "-button")}"]`);
  }

  chevronFor(button) {
    return button.querySelector("svg[data-chevron]");
  }

  // The drawer itself arrives expanded, so its button is all that is left to style. Every
  // button is told its state here rather than in the template, because a button renders
  // before the drawer beneath it has decided whether it holds the current page.
  setExpanded() {
    const open = this.element.querySelector("ul[data-nav-expanded='true']");

    this.dropdowns().forEach(({ menu, button }) => {
      const expanded = menu === open;
      button.setAttribute("aria-expanded", String(expanded));
      if (!expanded) return;

      button.classList.add("bg-gray-900");
      this.chevronFor(button)?.classList.add("rotate-180");
    });
  }

  disableButton(event) {
    const target = event.target.closest("a");
    if (target) {
      target.classList.add("disabled");
      target.style.pointerEvents = "none";
      target.style.opacity = "0.5";
    }
  }

  handleDropdownClick(event) {
    const button = event.target.closest("[id$='-button']");
    if (!button) return;

    const dropdowns = this.dropdowns();

    dropdowns.forEach(({ menu, button: other }) => {
      if (other !== button) {
        this.closeDropdown(menu, other, this.chevronFor(other));
      }
    });

    const current = dropdowns.find(({ button: candidate }) => candidate === button);
    if (!current) {
      this.unselectButton(button);
      return;
    }

    const chevron = this.chevronFor(button);
    if (current.menu.classList.contains("open")) {
      this.closeDropdown(current.menu, button, chevron);
    } else {
      this.openDropdown(current.menu, button, chevron);
    }
  }

  openDropdown(menu, button, chevron) {
    menu.classList.add("open");
    button.classList.add("bg-gray-900");
    button.setAttribute("aria-expanded", "true");
    menu.style.maxHeight = menu.scrollHeight + "px";
    menu.style.opacity = "1";
    menu.style.transform = "translateY(0)";
    if (chevron) {
      chevron.classList.add('rotate-180');
    }
  }

  closeDropdown(menu, button, chevron) {
    if (menu) {
      menu.classList.remove("open");
      menu.style.maxHeight = "0";
      menu.style.opacity = "0";
      menu.style.transform = "translateY(-10px)";
    }
    if (chevron) {
      chevron.classList.remove('rotate-180');
    }
    this.unselectButton(button);
  }

  unselectButton(button) {
    button.classList.remove("bg-gray-900");
    button.setAttribute("aria-expanded", "false");
  }
}
